import React from "react";
import BaseModel from "../models/BaseModel";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";

import Logger from "../Logger";
import type ELCB from "../models/ELCB";
import Fuse from "../models/Fuse";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import { itx } from "../translationItx/itx";
import Floor from "../models/Floor";
import { channelTypeInfo } from "../models/ChannelType";
import Room from "../models/Room";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import { EventKey } from "../eventLogger/EventKeys";

const logger = new Logger();

export default class ELCBTreeViewModel extends TreeViewModel {
  get typedObj(): ELCB {
    return this.obj as ELCB;
  }

  get isFolder(): boolean {
    return true;
  }

  getTagNeeded(): boolean {
    return true;
  }

  get childCollection(): Array<BaseModel> | undefined {
    return this.typedObj.fuses.sortBy("tagOrName");
  }

  get iconUrl(): string | undefined {
    if (this.typedObj) {
      return state.hostBaseUrl + "images/fuse/elcb.png"; // no itx
    }

    return undefined;
  }

  handleAdd() {
    try {
      const newFuse = new Fuse({}, this.typedObj);
      newFuse.tag = state.nameAndTagManager.generateTag(newFuse);

      this.typedObj.fuses.add(newFuse);
      state.eventLogger.addEvent(EventKey.ObjectCreated, {
        objectType: "Fuse",
        obj: newFuse.fullTag + " " + newFuse.name,
      });
      state.selectionManager.selectedObject = newFuse;
      return newFuse;
    } catch (ex) {
      console.error(ex);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //super.inlineButton_Properties(),
  ////////////////////////////////////////////////////////////////////////////////////////

  getButtons(): JSX.Element[] | undefined {
    return [
      <TreeMenuIcon
        // tooltipKey={tooltipKey}
        tooltipText={itx("terminalStripPropertiesView.numberNew", "Neu nummerieren")}
        onClick={() => {
          state.dialogManager.openDialog(
            <ConfirmDialog
              text={itx("terminalStripPropertiesView.numberNew", "Neu nummerieren")}
              title={itx("terminalStripPropertiesView.asknumberNew", "Sicherungen wiklich neu nummerieren?")}
              onOk={() => {
                this.typedObj.renumber();
                return true;
              }}
              onAbort={() => {
                return true;
              }}
              onClose={() => {
                return true;
              }}
            />,
            true,
          );
        }}
        iconName="sort-numeric-down" // no itx
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "numberNew"} // no itx, must be unique vs. inlineButton_Add's key
      />,
      super.inlineButton_Add("ContextMenuLables.addFuse"), // no itx
    ];
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // context menu
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  get contextMenuItems(): any {
    var subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData.items[0].name;
    } catch (ex) {
      logger.error(ex);
    }

    return {
      items: [
        {
          key: "generateAdresses",
          label: itx("treeViewModel.renumberFuses", "Sicherungen neu nummerieren"),
          iconName: "list-ol", // no itx
          onClick: async () => {
            this.typedObj.renumber();
          },
        },
        this.baseContextMenuItems_Add("ContextMenuLables.addFuse"), // no itx
        this.baseContextMenuItems_Delete(),
      ],
    };
  }
  ////////////////////////////////////////////////////////////////////////////////////////
  canBePastedInteractive(copiedObjClassName: string): boolean {
    return false;
  }
  allowDrag(): boolean {
    return true;
  }
  ////////////////////////////////////////////////////////////
  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    function createFuseForRoom(room: Room, elcb: ELCB): Array<Fuse> {
      let fuses: Array<Fuse> = [];
      let lightsAndOutlets = room.consumers
        .asArray()
        .filter((c) => channelTypeInfo(c.channelType).isLight || c.image.toLocaleLowerCase().includes("plug"));
      let rollos = room.consumers.asArray().filter((c) => channelTypeInfo(c.channelType).isRollo);

      if (lightsAndOutlets.length > 0) {
        let fuse = new Fuse({}, elcb);
        elcb.fuses.add(fuse);
        fuse.parent = elcb;
        fuse.name = room.name + itx("ELCBTreeviewModel.lichtersteckdosen", " Lichter/Steckdosen");
        fuses.push(fuse);

        lightsAndOutlets.forEach((c) => {
          fuse.treeViewModel.handleDrop2(mode, c);
        });
        fuse.tag = state.nameAndTagManager.generateTag(fuse);
      }

      if (rollos.length > 0) {
        let fuse = new Fuse({}, elcb);
        elcb.fuses.add(fuse);
        fuse.parent = elcb;
        fuse.name = room.name + itx("ELCBTreeviewModel.rollo", " Rollo");
        fuses.push(fuse);
        rollos.forEach((c) => {
          fuse.treeViewModel.handleDrop2(mode, c);
        });
        fuse.tag = state.nameAndTagManager.generateTag(fuse);
      }

      return fuses;
    }

    switch (obj.className) {
      case "Floor": {
        const floor = obj as Floor;
        let createdFuses: Array<Fuse> = [];
        // connect all light
        floor.rooms.sortBy("tag").forEach((r) => {
          createdFuses = createdFuses.concat(createFuseForRoom(r, this.typedObj));
        });

        if (createdFuses.length > 0) {
          this.typedObj.name = this.typedObj.name + " " + floor.name;
          state.selectionManager.selectedObject = createdFuses[0];
        }
        return new DropResult(createdFuses.length > 0);
      }
      case "Room": {
        const room = obj as Room;
        const fuses = createFuseForRoom(room, this.typedObj);
        if (fuses.length > 0) {
          this.typedObj.name = this.typedObj.name + " " + room.name;
          state.selectionManager.selectedObject = fuses[0];
        }
        return new DropResult(fuses.length > 0);
      }
      case "Fuse": {
        return state.undoManager.runAsSingleStep(itx("ELCBTreeviewModel.moveFuse", "Sicherung verschieben"), () => {
          const fuse = obj as Fuse;
          // change Parent
          const oldParent = fuse.parent as ELCB;
          fuse.parent = this.typedObj as ELCB;
          oldParent?.fuses.remove(fuse);
          (this.typedObj as ELCB).fuses.add(fuse);
          // retag under the new elcb, otherwise the old tag (e.g. "F1.5") stays orphaned
          // under the new elcb and corrupts numbering of subsequently added fuses
          fuse.tag = state.nameAndTagManager.generateTag(fuse);
          oldParent?.raiseOnChange("childCollection");
          (this.typedObj as ELCB).raiseOnChange("childCollection");
          return new DropResult(true);
        });
      }
    }

    return super.handleDrop2(mode, obj);
  }
}
